import React, { useState } from "react";

import { useTranslation } from "react-i18next";

import LiveConnectionCard from "./LiveConnectionCard";
import LivePreviewCard from "./LivePreviewCard";
import LiveQuotaCard from "./LiveQuotaCard";
import LiveRecapCard from "./LiveRecapCard";
import LiveReplayCard from "./LiveReplayCard";
import LiveSettingsCard from "./LiveSettingsCard";
import LiveStatusBar from "./LiveStatusBar";

import type { JSX } from "react";

import type { LiveReplayRange, UseLiveRegieResult } from "./live.types";

interface LiveRegieProps {
  regie: UseLiveRegieResult;
}

function LiveRegie({ regie }: LiveRegieProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const {
    view,
    settings,
    actions,
    audioOnly,
    allowLiveOkast,
    allowReplay,
    liveEncodingPlan,
    isArchived,
    quota,
  } = regie;
  const { phase, state } = view;

  const total = Math.max(1, settings.durationSeconds);
  const initialRange: LiveReplayRange = view.replayRangeMs
    ? {
        start: view.replayRangeMs.start / 1000,
        end: view.replayRangeMs.end / 1000,
      }
    : { start: 0, end: total };
  const [edited, setEdited] = useState<LiveReplayRange | null>(null);
  const range = edited ?? initialRange;

  // Drop the local edit when the server range (or the recorded duration) changes.
  const rangeKey = `${state}:${initialRange.start}:${initialRange.end}`;
  const [syncedKey, setSyncedKey] = useState(rangeKey);
  if (syncedKey !== rangeKey) {
    setSyncedKey(rangeKey);
    setEdited(null);
  }

  const isRangeModified =
    range.start !== initialRange.start || range.end !== initialRange.end;
  const showSource = !audioOnly && allowLiveOkast;
  const showQuality = !audioOnly;
  const troubleshootingUrl = t("editor-help:live-troobleshooting-guide:link");
  const isPreparation = phase === "preparation";
  const isEditable = isPreparation && !isArchived;
  const settingsCard = (
    <LiveSettingsCard
      isEditable={isEditable}
      liveEncodingPlan={liveEncodingPlan}
      onChange={actions.updateSettings}
      settings={settings}
      showQuality={showQuality}
      showReplay={allowReplay}
      showSource={showSource}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <LiveStatusBar
        range={range}
        regie={regie}
        troubleshootingUrl={troubleshootingUrl}
      />
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-4">
          <LivePreviewCard
            isRangeModified={isRangeModified}
            onRangeChange={setEdited}
            range={range}
            regie={regie}
          />
          {phase !== "termine" ? (
            <p className="text-muted-foreground text-sm">
              {t("liveRegie_problem")}{" "}
              <a
                className="text-primary underline"
                href={troubleshootingUrl}
                rel="noreferrer"
                target="_blank"
              >
                {t("liveRegie_troubleshootingGuide")}
              </a>
            </p>
          ) : null}
        </div>
        <div className="flex min-w-0 flex-[1_1_380px] flex-col gap-4">
          {isPreparation ? (
            <React.Fragment>
              {settingsCard}
              <LiveQuotaCard quota={quota} />
            </React.Fragment>
          ) : null}
          {phase === "connexion" || phase === "antenne" ? (
            <React.Fragment>
              <LiveConnectionCard view={view} />
              {settingsCard}
            </React.Fragment>
          ) : null}
          {phase === "termine" ? (
            <React.Fragment>
              <LiveReplayCard range={range} view={view} />
              <LiveRecapCard
                settings={settings}
                showQuality={showQuality}
                showSource={showSource}
                view={view}
              />
            </React.Fragment>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default LiveRegie;
